import dayjs, { Dayjs } from 'dayjs';

/**
 * 临时任务「任务日期 / 出发时间」可选范围计算
 *
 * 一、背景
 * 创建临时任务抽屉里有两个日期选择器：任务日期、出发时间。业务规则是「发车只能在任务日当天或次日」，
 * 外层再套一个全局窗口：任务日期最早昨天、出发日最早今天、两者最晚都是今天 + 3。
 * 两个选择器的 disabledDate 互相联动：选了出发时间，任务日期只能是出发日当天或前一天；
 * 选了任务日期，出发日只能是任务日期当天或后一天。
 *
 * 二、出过什么问题
 * 浏览器处于有夏令时的时区（美国各州）时，切换日前后几天日历格子会灰错：
 *   - 2026-03-08 拨快当天：「今天」灰掉，49f950ad 用 isBefore(today, 'day') 修过一次
 *   - 2026-04-30 加了上界 today + 3 之后：拨快日前 1 到 2 天，任务日期空着时「今天 + 3」灰掉
 *   - 2026-05-06 加了出发时间联动之后：回拨日前 1 到 3 天，出发时间已选、任务日期已有值，
 *     再打开任务日期日历，与出发同日的格子灰掉
 * 国内同事浏览器是 UTC+8、没有夏令时，复现不了，所以一直没人报。
 *
 * 三、为什么会错
 * 项目全局加载了 dayjs 的 timezone 插件，antd 的日期组件一检测到该插件，取「现在」就改用 dayjs().tz()。
 * 这种实例带一个固定的 $offset 字段，记的是打开日历那一刻的时差。日历格子、用户点选后拿到的 Dayjs 值
 * 都从它派生，$offset 原样复制。跨过切换日后，对象记的时差和那天的真实时差差 1 小时，
 * dayjs 算时间戳时会按这 1 小时修正：format 出来的年月日仍然正确，但 valueOf / isBefore / isAfter
 * 这类按时间戳比大小的操作会偏 1 小时。startOf('day') 的零点偏到前一天 23:00 或当天 01:00，
 * 再拿去当上下界，就差出一天。
 *
 * 四、这里怎么做
 * 所有「按天」的范围判定先 format('YYYY-MM-DD') 退化成字符串，再比字符串大小。
 * 年月日字符串的字典序就是日期序；format 只抄年月日这几个字，不经过时间戳，带时差的对象在这一步被甩掉。
 * 以字符串新建的 dayjs 实例没有 $offset，add(n, 'day') 按墙上日期走，跨切换日不偏。
 * 因此本文件不对 DatePicker 给出的 Dayjs 实例做 startOf / add / subtract / isBefore / isAfter。
 *
 * 详细分析、穷举脚本与复现步骤见 docs/plans/33-tms-date-dst。
 */
export const DAY_FORMAT = 'YYYY-MM-DD';

export interface DayRange {
    minDay: string;
    maxDay: string;
}

/** 取 Dayjs 实例的墙上日期字符串，format 只读年月日字段，不受 $offset 影响 */
export const toDay = (value: Dayjs): string => value.format(DAY_FORMAT);

/** 日期字符串加减天数；以字符串新建的实例无 $offset，add 按墙上日期走，跨切换日不偏 */
export const addDays = (day: string, days: number): string => dayjs(day).add(days, 'day').format(DAY_FORMAT);

const maxDay = (left: string, right: string): string => (left > right ? left : right);
const minDay = (left: string, right: string): string => (left < right ? left : right);

/**
 * 任务日期范围：[today-1, today+3]
 * 已选出发时间时收窄为 [max(today-1, departure-1), min(today+3, departure)]
 */
export const getTaskDateRange = (todayDay: string, departureDay?: string | null): DayRange => {
    let range: DayRange = { minDay: addDays(todayDay, -1), maxDay: addDays(todayDay, 3) };
    if (departureDay) {
        range = {
            minDay: maxDay(range.minDay, addDays(departureDay, -1)),
            maxDay: minDay(range.maxDay, departureDay),
        };
    }
    return range;
};

/**
 * 出发时间日期范围：[today, today+3]
 * 已选任务日期时收窄为 [max(today, taskDate), min(today+3, taskDate+1)]
 */
export const getDepartureDateRange = (todayDay: string, taskDay?: string | null): DayRange => {
    let range: DayRange = { minDay: todayDay, maxDay: addDays(todayDay, 3) };
    if (taskDay) {
        range = {
            minDay: maxDay(range.minDay, taskDay),
            maxDay: minDay(range.maxDay, addDays(taskDay, 1)),
        };
    }
    return range;
};

/** YYYY-MM-DD 字符串字典序即日期序 */
export const isDayOutOfRange = (day: string, range: DayRange): boolean => day < range.minDay || day > range.maxDay;
